import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography, Alert, Paper, List, ListItem, ListItemText, Checkbox, Divider, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Card, CardContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import apiAxiosInstance from '../../utils/axiosInstance';
import { useTeacher } from '../../context/TeacherContext';

interface Course {
  id: string;
  name: string;
}

interface PreviewMember {
  userId: string;
  firstName: string;
  lastName: string;
}

interface PreviewGroup {
  source: string;
  courseId: string;
  groupName: string;
  members: PreviewMember[];
}

interface DuplicateItem {
  existing: { _id: string; firstName: string; lastName: string; studentId: string; gradeLevel: string };
  proposed: { firstName: string; lastName: string; studentId: string; gradeLevel: string; groupName: string };
}

interface GoogleClassroomImportProps {
  onStudentsAdded?: () => void;
}

const GoogleClassroomImport: React.FC<GoogleClassroomImportProps> = ({ onStudentsAdded }) => {
  const { teacher } = useTeacher();
  const navigate = useNavigate();
  const [statusLoading, setStatusLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsAuth, setNeedsAuth] = useState(false); // app auth
  const [needsClassroomAuth, setNeedsClassroomAuth] = useState(false); // Google Classroom consent/scopes
  const [courses, setCourses] = useState<Course[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);
  const [createGroupsFromCourses, setCreateGroupsFromCourses] = useState(true);

  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewGroups, setPreviewGroups] = useState<PreviewGroup[]>([]);

  const [importLoading, setImportLoading] = useState(false);
  const [duplicates, setDuplicates] = useState<DuplicateItem[]>([]);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [missingScopes, setMissingScopes] = useState<string[]>([]);
  const [resolvedDuplicates, setResolvedDuplicates] = useState<DuplicateItem['proposed'][]>([]);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        setStatusLoading(true);
        const res = await apiAxiosInstance.get('/api/google-classroom/status');
        setConnected(!!res.data.connected);
        if (Array.isArray(res.data?.missingScopes)) {
          setMissingScopes(res.data.missingScopes);
          if (res.data.missingScopes.length > 0) setNeedsClassroomAuth(true);
        }
      } catch (e) {
        // If not authenticated, prompt to sign in
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const err: any = e;
        if (err?.response?.status === 401) {
          setNeedsAuth(true);
          setError(null);
        } else {
          setError('Failed to check Google Classroom status');
        }
      } finally {
        setStatusLoading(false);
      }
    };
    checkStatus();
  }, []);

  const handleConnect = () => {
    // Fetch OAuth URL from backend then redirect
    (async () => {
      try {
        const res = await apiAxiosInstance.get('/api/google-classroom/auth/url');
        const { url } = res.data || {};
        if (url) {
          window.location.href = url;
        } else {
          setError('Failed to initiate Google Classroom authorization');
        }
      } catch (e) {
        setError('Failed to initiate Google Classroom authorization');
      }
    })();
  };

  const handleSignInWithGoogle = () => {
    try {
      sessionStorage.setItem('redirectPath', '/add-students');
    } catch {}
    const base = process.env.NODE_ENV === 'production'
      ? (process.env.REACT_APP_AUTH_URL || 'https://teach-ai-db-backend.vercel.app/auth')
      : (process.env.REACT_APP_AUTH_URL ? `${process.env.REACT_APP_AUTH_URL}/auth` : 'http://localhost:5000/auth');
    window.location.href = `${base}/google`;
  };

  const handleSignInWithEmail = () => {
    navigate('/login');
  };

  const fetchCoursesAndStudents = async () => {
    try {
      setCoursesLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      // Fetch courses
      const res = await apiAxiosInstance.get('/api/google-classroom/courses');
      const items: Course[] = (res.data.courses || []).map((c: any) => ({ id: c.id, name: c.name }));
      setCourses(items);
      
      if (!items.length) {
        setError('No Google Classroom courses found. Make sure you have created classes in Google Classroom.');
        return;
      }
      
      // Auto-select all courses and fetch students immediately
      const allCourseIds = items.map(c => c.id);
      setSelectedCourseIds(allCourseIds);
      
      // Build preview immediately
      const selections = items.map(c => ({ courseId: c.id, courseName: c.name }));
      const previewRes = await apiAxiosInstance.post('/api/google-classroom/preview', { selections });
      const groups = previewRes.data.groups || [];
      setPreviewGroups(groups);
      
      if (groups.length === 0) {
        setError('No students found in your Google Classroom courses. Make sure students are enrolled.');
      } else {
        const totalStudents = groups.reduce((sum: number, g: PreviewGroup) => sum + g.members.length, 0);
        setSuccessMessage(`Found ${totalStudents} student${totalStudents === 1 ? '' : 's'} across ${groups.length} course${groups.length === 1 ? '' : 's'}. Review the students below and click Import when ready.`);
      }
    } catch (e: any) {
      console.error('Fetch courses and students error:', e);
      if (e?.response?.status === 401) {
        setNeedsClassroomAuth(true);
        const ms = e?.response?.data?.missingScopes;
        if (Array.isArray(ms)) setMissingScopes(ms);
        setError('Google Classroom access not authorized. Please connect and grant permissions.');
      } else {
        const errorMsg = e?.response?.data?.error || 'Failed to load Google Classroom courses and students';
        setError(errorMsg);
      }
    } finally {
      setCoursesLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_classroom') === 'connected') {
      setConnected(true);
      setError(null);
      setSuccessMessage('Successfully connected to Google Classroom! Loading your students...');
      // Clear the URL parameter to avoid confusion
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      // Automatically load students after connection
      fetchCoursesAndStudents();
    }
  }, []);

  // Auto-load students when connected (but not from OAuth callback)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isOAuthCallback = params.get('google_classroom') === 'connected';
    
    // Only auto-load if connected, not already loading/loaded, and not from OAuth callback
    if (connected && !isOAuthCallback && previewGroups.length === 0 && !coursesLoading && !statusLoading) {
      fetchCoursesAndStudents();
    }
  }, [connected, statusLoading]);

  const toggleCourse = (courseId: string) => {
    setSelectedCourseIds(prev => prev.includes(courseId) ? prev.filter(id => id !== courseId) : [...prev, courseId]);
  };

  const startImport = async () => {
    try {
      setImportLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      // Modify groups based on createGroupsFromCourses setting
      const groupsToImport = createGroupsFromCourses ? previewGroups : previewGroups.map(g => ({ ...g, groupName: '' }));
      
      console.log('=== Starting Google Classroom Import ===');
      console.log('Groups to import:', {
        count: groupsToImport.length,
        groups: groupsToImport.map(g => ({
          name: g.groupName,
          memberCount: g.members?.length || 0
        })),
        createGroups: createGroupsFromCourses
      });
      
      const res = await apiAxiosInstance.post('/api/google-classroom/import', { 
        groups: groupsToImport,
        createGroups: createGroupsFromCourses 
      });
      
      console.log('=== Import Success ===');
      console.log('Response:', res.data);
      
      const dups: DuplicateItem[] = res.data.duplicates || [];
      
      const skippedExisting = res.data.skippedExisting || 0;
      
      if (dups.length) {
        setDuplicates(dups);
        setResolvedDuplicates(dups.map(d => ({ ...d.proposed })));
        setDuplicateDialogOpen(true);
        
        // Show summary even when there are duplicates to resolve
        if (res.data.createdStudents > 0 || skippedExisting > 0) {
          const parts = [];
          if (res.data.createdStudents > 0) {
            parts.push(`${res.data.createdStudents} new student${res.data.createdStudents === 1 ? '' : 's'} imported`);
          }
          if (skippedExisting > 0) {
            parts.push(`${skippedExisting} existing student${skippedExisting === 1 ? '' : 's'} added to groups`);
          }
          setSuccessMessage(parts.join(', ') + '. Please review conflicts below.');
        }
      } else {
        const parts = [];
        
        if (res.data.createdStudents > 0) {
          parts.push(`${res.data.createdStudents} new student${res.data.createdStudents === 1 ? '' : 's'} imported`);
        }
        
        if (skippedExisting > 0) {
          parts.push(`${skippedExisting} existing student${skippedExisting === 1 ? '' : 's'} already in your roster (added to groups)`);
        }
        
        if (createGroupsFromCourses && res.data.createdGroups > 0) {
          parts.push(`${res.data.createdGroups} group${res.data.createdGroups === 1 ? '' : 's'} created`);
        }
        
        if (parts.length === 0) {
          setSuccessMessage('Import completed! All students were already in your roster.');
        } else {
          setSuccessMessage(`Import completed! ${parts.join(', ')}.`);
        }
      }
      
      // Trigger refresh callback if provided
      if (onStudentsAdded && (res.data.createdStudents > 0 || res.data.skippedExisting > 0)) {
        onStudentsAdded();
      }
    } catch (e: any) {
      console.error('=== Import Error (Browser Console) ===');
      console.error('Error object:', e);
      console.error('Response status:', e?.response?.status);
      console.error('Response data:', e?.response?.data);
      console.error('Error message:', e?.message);
      console.error('Request URL:', e?.config?.url);
      console.error('Request method:', e?.config?.method);
      console.error('Request payload:', e?.config?.data);
      
      const errorMsg = e?.response?.data?.error || 'Failed to import from Google Classroom';
      const errorDetails = e?.response?.data?.details || e?.response?.data?.message;
      const fullErrorMsg = errorDetails ? `${errorMsg}: ${errorDetails}` : errorMsg;
      setError(fullErrorMsg);
    } finally {
      setImportLoading(false);
    }
  };

  const applyResolvedDuplicates = async () => {
    try {
      setImportLoading(true);
      setError(null);
      
      const res = await apiAxiosInstance.post('/api/google-classroom/resolve-duplicates', { resolvedStudents: resolvedDuplicates });
      setDuplicateDialogOpen(false);
      setDuplicates([]);
      setSuccessMessage(`Duplicates resolved! Created ${res.data.created || 0} students with updated information.`);
      
      // Trigger refresh callback if provided
      if (onStudentsAdded && (res.data.created > 0 || res.data.updated > 0)) {
        onStudentsAdded();
      }
    } catch (e: any) {
      console.error('Resolve duplicates error:', e);
      const errorMsg = e?.response?.data?.error || 'Failed to resolve duplicate students';
      setError(errorMsg);
    } finally {
      setImportLoading(false);
    }
  };

  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>
      )}

      {!connected ? (
        <Box>
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>Connect your Google Classroom to import students and groups.</Typography>
            <Button variant="contained" onClick={handleConnect} disabled={statusLoading} startIcon={statusLoading ? <CircularProgress size={18} /> : undefined}>
              {statusLoading ? 'Checking…' : (needsClassroomAuth ? 'Retrieve Students from Classroom' : 'Connect Google Classroom')}
            </Button>
            {needsAuth && (
              <>
                <Alert severity="info" sx={{ mb: 2, mt: 2 }}>Please sign in to continue.</Alert>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="contained" onClick={handleSignInWithGoogle}>Sign in with Google</Button>
                  <Button variant="outlined" onClick={handleSignInWithEmail}>Sign in with Email</Button>
                </Box>
              </>
            )}
          </>
        </Box>
      ) : (
        <Box>
          {previewGroups.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {coursesLoading ? (
                <>
                  <CircularProgress size={24} />
                  <Typography variant="body1">
                    Loading your Google Classroom courses and students...
                  </Typography>
                </>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No students loaded yet. If nothing appears, please refresh the page.
                </Typography>
              )}
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Students from Google Classroom</Typography>
              
              {/* Group Creation Option */}
              <Card sx={{ mb: 3, bgcolor: 'info.light', borderLeft: 4, borderColor: 'info.main' }}>
                <CardContent>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={createGroupsFromCourses}
                        onChange={(e) => setCreateGroupsFromCourses(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Create groups from course names
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {createGroupsFromCourses 
                            ? 'Groups will be automatically created from your Google Classroom course names. You can still add students to other groups or make modifications after import.'
                            : 'Students will be imported without creating groups. You can manually organize them into groups after import.'}
                        </Typography>
                      </Box>
                    }
                  />
                </CardContent>
              </Card>

              {/* Students List by Course */}
              <Box sx={{ mb: 3 }}>
                {previewGroups.map((group, idx) => (
                  <Paper 
                    key={idx} 
                    variant="outlined" 
                    sx={{ mb: 2, overflow: 'hidden' }}
                  >
                    {/* Course Header */}
                    <Box 
                      sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'primary.contrastText', 
                        p: 2 
                      }}
                    >
                      <Typography variant="h6">
                        {group.groupName}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {group.members.length} student{group.members.length === 1 ? '' : 's'}
                      </Typography>
                    </Box>

                    {/* Students List */}
                    <List>
                      {group.members.map((member, memberIdx) => (
                        <React.Fragment key={memberIdx}>
                          <ListItem>
                            <ListItemText 
                              primary={`${member.firstName} ${member.lastName}`}
                              secondary={`Student ID: ${member.userId}`}
                            />
                          </ListItem>
                          {memberIdx < group.members.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Paper>
                ))}
              </Box>

              {/* Import Actions */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  onClick={startImport} 
                  disabled={importLoading}
                  size="large"
                >
                  {importLoading ? 'Importing…' : 'Import Students'}
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={fetchCoursesAndStudents} 
                  disabled={coursesLoading}
                >
                  {coursesLoading ? 'Refreshing…' : 'Refresh'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Dialog open={duplicateDialogOpen} onClose={() => setDuplicateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Resolve Duplicate Students</DialogTitle>
        <DialogContent>
          {duplicates.map((d, idx) => (
            <Box key={idx} sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Existing: {d.existing.firstName} {d.existing.lastName} (ID: {d.existing.studentId})
              </Typography>
              {(d.existing as any).note && (
                <Alert severity={d.existing._id === 'ghost-record' ? 'warning' : 'info'} sx={{ mb: 2 }}>
                  {(d.existing as any).note}
                  {d.existing._id === 'ghost-record' && (
                    <Typography variant="body2" sx={{ mt: 1, fontSize: '0.875rem' }}>
                      <strong>Solution:</strong> Modify the Student ID below (e.g., add a suffix like "-2" or "-new") to create this student with a new unique ID.
                    </Typography>
                  )}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <TextField 
                  size="small" 
                  label="First Name" 
                  value={resolvedDuplicates[idx]?.firstName || ''} 
                  onChange={e => setResolvedDuplicates(prev => prev.map((p, i) => i === idx ? { ...p, firstName: e.target.value } : p))} 
                />
                <TextField 
                  size="small" 
                  label="Last Name" 
                  value={resolvedDuplicates[idx]?.lastName || ''} 
                  onChange={e => setResolvedDuplicates(prev => prev.map((p, i) => i === idx ? { ...p, lastName: e.target.value } : p))} 
                />
                <TextField 
                  size="small" 
                  label="Student ID" 
                  value={resolvedDuplicates[idx]?.studentId || ''} 
                  onChange={e => setResolvedDuplicates(prev => prev.map((p, i) => i === idx ? { ...p, studentId: e.target.value } : p))}
                  helperText={d.existing._id === 'ghost-record' ? 'Modify this ID to work around the ghost record' : undefined}
                  error={d.existing._id === 'ghost-record'}
                  sx={{ flexGrow: 1 }}
                />
                <TextField 
                  size="small" 
                  label="Grade Level" 
                  value={resolvedDuplicates[idx]?.gradeLevel || (teacher?.gradeLevel || '')} 
                  onChange={e => setResolvedDuplicates(prev => prev.map((p, i) => i === idx ? { ...p, gradeLevel: e.target.value } : p))} 
                />
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateDialogOpen(false)}>Cancel</Button>
          <Button onClick={applyResolvedDuplicates} variant="contained">Save Students</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GoogleClassroomImport;


